import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  Button,
  Icon,
  Illustration,
  Loader,
  Typography,
} from '@saviynt/design-system';

import { useInvertedLocalize } from '../../../../shared/hooks';
import AlertService from '../../../../shared/services/AlertService';
import { alertQueueActions } from '../../../../shared/store/alertQueueSlice';
import { useGetLicenseStatusQuery } from '../../data-layer/licenseManagementApi';
import useLicenseExportReport from '../../hooks/use-licenseExportReport';
import useLicenseExportStatusPolling from '../../hooks/use-licenseExportStatusPolling';
import { LICENSE_INTELLIGENCE_IDENTITY_DISTRIBUTION_DATA } from '../../mock-data/licenseIntelligenceIdentityDistributionData';
import { LICENSE_INTELLIGENCE_LICENCE_INVENTORY_DATA } from '../../mock-data/licenseIntelligenceLicenceInventoryData';
import { LICENSE_INTELLIGENCE_UNIT_CONSUMPTION_DATA } from '../../mock-data/licenseIntelligenceUnitConsumptionData';
import { LICENSE_INTELLIGENCE_UNIT_CONSUMPTION_TREND_DATA } from '../../mock-data/licenseIntelligenceUnitConsumptionTrendData';
import { LICENSE_INTELLIGENCE_VENDOR_ENDPOINTS_DATA } from '../../mock-data/licenseIntelligenceVendorEndpointsData';
import type { RoleIntelligenceRow } from '../../mock-data/roleIntelligenceRolesTableMockData';
import type { LicenseInventoryApiData, UnitConsumptionData } from '../../types';

import type { FUEInventoryRow } from './FUEInventory/FUEInventoryTable/constants/fueInventoryTableConstants';
import type { LicenseFormData } from './Overview/AddLicensePanel/type';
import { AddLicenseModal } from './AddLicenseModal';
import {
  FUEInventoryControls,
  FUEInventoryKPIs,
  FUEInventoryTable,
  FUEInventoryUserPanel,
  LicenseExportBanner,
  RunStarSimulationModal,
} from './FUEInventory';
import {
  AddLicensePanel,
  OptimizationFindings,
  OverviewContent,
  OverviewTitle,
} from './Overview';
import {
  RoleIntelligenceKPIs,
  RoleIntelligencePanel,
} from './RoleIntelligence';
import { SaveLicenseConfirmModal } from './SaveLicenseConfirmModal';
import { TabGroup } from './TabGroup';
import { TitleSection } from './TitleSection';

import './Details.css';

interface RouteParams {
  vendor?: string;
}

/**
 * Displays detailed information about a specific license vendor in the
 * License Intelligence dashboard. Provides tabbed navigation for overview, FUE inventory,
 * and role intelligence views. Reads the vendor from the URL route parameter and renders
 * vendor-specific data including KPIs, optimization findings, tables, and side panels.
 * Queries license status to conditionally show an add-license modal on first visit.
 * @returns {React.ReactElement} The license details view with tabbed content and side panels
 */
function Details(): React.ReactElement {
  const dispatch = useDispatch();
  const localize = useInvertedLocalize();
  const [activeTab, setActiveTab] = useState('overview');
  const [isAddLicensePanelOpen, setIsAddLicensePanelOpen] = useState(false);
  const [showAddLicenseModal, setShowAddLicenseModal] = useState(true);
  const [didSkipLicenseSetup, setDidSkipLicenseSetup] = useState(false);
  const [isFirstSimulationPending, setIsFirstSimulationPending] =
    useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [pendingLicenseData, setPendingLicenseData] =
    useState<LicenseFormData | null>(null);

  // Mock save license mutation
  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  const saveLicense = (_payload?: unknown) => ({
    unwrap: () =>
      Promise.resolve({
        data: { cloudLicenseKey: 9, created: true },
        message: 'License created',
      }),
  });
  const isSavingLicense = false;

  // Mock run simulation mutation
  // eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
  const runSimulation = (_payload?: unknown) => ({
    unwrap: () => Promise.resolve({ message: 'Simulation started' }),
  });
  const isRunningSimulation = false;

  // License Export hooks
  const { exportLicenseInventory, isExporting } = useLicenseExportReport();

  useLicenseExportStatusPolling();

  // Set dark theme on documentElement so the global header inherits dark mode styles
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'dark');
    document.body.style.backgroundColor = '#0F1419';
    document.documentElement.style.scrollbarWidth = 'thin';

    return () => {
      document.documentElement.removeAttribute('data-theme');
      document.body.style.backgroundColor = '';
      document.documentElement.style.scrollbarWidth = '';
    };
  }, []);
  const [selectedFUEUser, setSelectedFUEUser] =
    useState<FUEInventoryRow | null>(null);
  const [isFUEUserPanelOpen, setIsFUEUserPanelOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<RoleIntelligenceRow | null>(
    null
  );
  const [isRolePanelOpen, setIsRolePanelOpen] = useState(false);
  const [isStarSimulationModalOpen, setIsStarSimulationModalOpen] =
    useState(false);
  const [isFUEFilterBarOpen, setIsFUEFilterBarOpen] = useState(false);
  const [fueFilterSelections, setFueFilterSelections] = useState<
    Record<string, string[]>
  >({});
  const [fueEnabledFilterIds, setFueEnabledFilterIds] = useState<string[]>([
    'status',
    'tier',
    'accountType',
  ]);
  const { vendor } = useParams<RouteParams>();

  const fueHasActiveFilters = Object.values(fueFilterSelections).some(
    (v) => v.length > 0
  );

  const handleFUEFilterChange = (filterId: string, values: string[]): void => {
    setFueFilterSelections((prev) => ({ ...prev, [filterId]: values }));
  };

  const handleFUEClearFilters = (): void => {
    setFueFilterSelections({});
  };

  /**
   * Convert URL-friendly vendor name to display name
   * Examples: 'sap' → 'SAP', 'office-365' → 'Office 365', 'aws' → 'AWS'
   */
  const getVendorDisplayName = (vendorParam?: string): string => {
    if (!vendorParam) {
      return 'SAP';
    }

    return vendorParam
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const currentVendor = getVendorDisplayName(vendor);

  // Check if user needs to add a license
  const { data: licenseStatusData } = useGetLicenseStatusQuery(currentVendor);

  // Using local mock data instead of API call
  const fueConsumptionData =
    LICENSE_INTELLIGENCE_UNIT_CONSUMPTION_DATA.data as UnitConsumptionData;
  const isFueLoading = false;
  const isFueFetching = false;
  const isFueError = false;
  const refetchFue = () => Promise.resolve();

  // Using local mock data instead of API call
  const licenseInventoryData =
    LICENSE_INTELLIGENCE_LICENCE_INVENTORY_DATA.data as unknown as LicenseInventoryApiData;
  const isInventoryLoading = false;
  const isInventoryFetching = false;
  const isInventoryError = false;
  const refetchInventory = () => Promise.resolve();

  // Using local mock data instead of API call
  const trendData = LICENSE_INTELLIGENCE_UNIT_CONSUMPTION_TREND_DATA.data;
  const isTrendLoading = false;
  const isTrendFetching = false;
  const isTrendError = false;
  const refetchTrend = () => Promise.resolve();

  // Using local mock data instead of API call
  const identityDistributionData =
    LICENSE_INTELLIGENCE_IDENTITY_DISTRIBUTION_DATA.data;
  const isDistributionLoading = false;
  const isDistributionFetching = false;
  const isDistributionError = false;
  const refetchDistribution = () => Promise.resolve();

  // Using local mock data instead of API call
  const vendorEndpoints = LICENSE_INTELLIGENCE_VENDOR_ENDPOINTS_DATA;

  const endpointOptions = vendorEndpoints.map((ep) => ({
    label: ep.endpointname,
    value: ep.endpointname,
  }));

  const shouldShowAddLicenseModal =
    showAddLicenseModal && licenseStatusData?.showAddLicenseModal === true;

  const handleDismissAddLicenseModal = (): void => {
    setShowAddLicenseModal(false);
    setDidSkipLicenseSetup(true);
  };

  const handleAddLicenseFromModal = (): void => {
    setIsAddLicensePanelOpen(true);
  };

  const handleTabChange = (tabId: string): void => {
    setActiveTab(tabId);
    setIsAddLicensePanelOpen(false);
    setIsFUEUserPanelOpen(false);
    setIsRolePanelOpen(false);
    setSelectedFUEUser(null);
    setSelectedRole(null);
  };

  const handleOpenAddLicensePanel = (): void => {
    setIsAddLicensePanelOpen(true);
  };

  const handleCloseAddLicensePanel = (): void => {
    setIsAddLicensePanelOpen(false);
  };

  const handleSaveLicense = (formData: LicenseFormData): void => {
    setPendingLicenseData(formData);

    const endpointKeys = (formData.endpointScope ?? [])
      .map((name) => vendorEndpoints.find((ep) => ep.endpointname === name)?.id)
      .filter((id): id is string => !!id)
      .map((id) => parseInt(id, 10))
      .filter((n) => !Number.isNaN(n));

    saveLicense({
      vendor: currentVendor.toLowerCase(),
      licenseData: formData,
      endpointKeys,
    })
      .unwrap()
      .then(() => {
        setIsConfirmModalOpen(true);
      })
      .catch(() => {
        dispatch(
          alertQueueActions.enqueue(
            AlertService.error(
              'Failed to save license.',
              'Please try again or contact support.'
            )
          )
        );
      });
  };

  const handleRunSimulation = (): void => {
    setIsConfirmModalOpen(false);

    const simulationEndpointKeys = vendorEndpoints
      .map((ep) => parseInt(ep.id, 10))
      .filter((n) => !Number.isNaN(n));

    runSimulation({
      cloudLicenseKey: 1,
      rulesetKey: 0,
      endpointKeys: simulationEndpointKeys,
      vendorType: currentVendor.toUpperCase(),
    })
      .unwrap()
      .then(() => {
        setIsAddLicensePanelOpen(false);
        setShowAddLicenseModal(false);
        setDidSkipLicenseSetup(false);
        setIsFirstSimulationPending(true);
        dispatch(
          alertQueueActions.enqueue(
            AlertService.info(
              'Simulation is running in background.',
              'Your license simulation has been queued and will complete shortly.'
            )
          )
        );
      })
      .catch(() => {
        dispatch(
          alertQueueActions.enqueue(
            AlertService.error(
              'Failed to start simulation.',
              'Please try again or contact support.'
            )
          )
        );
      });
  };

  const handleStartSimulation = (): void => {
    dispatch(
      alertQueueActions.enqueue(
        AlertService.success(
          'STAR simulation started.',
          'This may take some time and table will be updated after simulation is complete.'
        )
      )
    );
  };

  const renderMainContent = (): React.ReactElement => {
    if (shouldShowAddLicenseModal) {
      return (
        <>
          {!isAddLicensePanelOpen && (
            <AddLicenseModal
              onClose={handleDismissAddLicenseModal}
              onAddLicense={handleAddLicenseFromModal}
              onSkip={handleDismissAddLicenseModal}
            />
          )}
          {isAddLicensePanelOpen && (
            <div
              className='LicenseManagement-AddLicenseModal'
              data-theme='dark'
            />
          )}
          <AddLicensePanel
            isOpen={isAddLicensePanelOpen}
            onClose={handleCloseAddLicensePanel}
            onSave={handleSaveLicense}
            vendor={currentVendor.toLowerCase()}
            endpointOptions={endpointOptions}
            isSaving={isSavingLicense}
          />
        </>
      );
    }

    if (didSkipLicenseSetup) {
      return (
        <>
          <div className='LicenseManagement-Details-emptyState'>
            <div className='LicenseManagement-Details-emptyStateContent'>
              <Illustration kind='InfoGeneral' />
              <Typography kind='h1' color='--color-foreground-neutral-bold'>
                {localize('No data to display')}
              </Typography>
              <Typography
                kind='h3'
                color='--color-foreground-neutral-medium'
                className='LicenseManagement-Details-emptyStateDescription'>
                {localize(
                  'Add your first business application to start tracking license utilization, cost optimization opportunities, and compliance across your enterprise software providers.'
                )}
              </Typography>
              <Button
                kind='filled'
                size='medium'
                leftIcon={<Icon kind='Add' size='small' />}
                isDisabled={isAddLicensePanelOpen}
                onClick={handleOpenAddLicensePanel}>
                {localize('Add License')}
              </Button>
            </div>
          </div>

          {/* Add License Side Panel */}
          <AddLicensePanel
            isOpen={isAddLicensePanelOpen}
            onClose={handleCloseAddLicensePanel}
            onSave={handleSaveLicense}
            vendor={currentVendor.toLowerCase()}
            endpointOptions={endpointOptions}
            isSaving={isSavingLicense}
          />
        </>
      );
    }

    return (
      <>
        <TitleSection vendor={currentVendor} />
        <div className='LicenseManagement-Details-body'>
          <TabGroup activeTab={activeTab} onTabChange={handleTabChange} />
          <div className='LicenseManagement-Details-container'>
            {activeTab === 'overview' && (
              <>
                <OverviewTitle
                  onAddLicenseClick={handleOpenAddLicensePanel}
                  title={`${currentVendor.toUpperCase()} RISE FUE License Consumption`}
                />
                <OverviewContent
                  vendor={currentVendor}
                  fueConsumptionData={
                    isFirstSimulationPending ? undefined : fueConsumptionData
                  }
                  isFueLoading={isFirstSimulationPending ? false : isFueLoading}
                  isFueFetching={
                    isFirstSimulationPending ? false : isFueFetching
                  }
                  isFueError={isFirstSimulationPending ? false : isFueError}
                  refetchFue={() => {
                    refetchFue().catch(() => {});
                  }}
                  licenseInventoryData={
                    isFirstSimulationPending ? undefined : licenseInventoryData
                  }
                  isInventoryLoading={
                    isFirstSimulationPending ? false : isInventoryLoading
                  }
                  isInventoryFetching={
                    isFirstSimulationPending ? false : isInventoryFetching
                  }
                  isInventoryError={
                    isFirstSimulationPending ? false : isInventoryError
                  }
                  refetchInventory={() => {
                    refetchInventory().catch(() => {});
                  }}
                  trendData={isFirstSimulationPending ? undefined : trendData}
                  isTrendLoading={
                    isFirstSimulationPending ? false : isTrendLoading
                  }
                  isTrendFetching={
                    isFirstSimulationPending ? false : isTrendFetching
                  }
                  isTrendError={isFirstSimulationPending ? false : isTrendError}
                  refetchTrend={() => {
                    refetchTrend().catch(() => {});
                  }}
                  identityDistributionData={
                    isFirstSimulationPending
                      ? undefined
                      : identityDistributionData
                  }
                  isDistributionLoading={
                    isFirstSimulationPending ? false : isDistributionLoading
                  }
                  isDistributionFetching={
                    isFirstSimulationPending ? false : isDistributionFetching
                  }
                  isDistributionError={
                    isFirstSimulationPending ? false : isDistributionError
                  }
                  refetchDistribution={() => {
                    refetchDistribution().catch(() => {});
                  }}
                />
                <OptimizationFindings
                  vendor={currentVendor.toLowerCase()}
                  skip={isFirstSimulationPending}
                />
              </>
            )}
            {activeTab === 'fue-inventory' && (
              <>
                <LicenseExportBanner />
                <FUEInventoryKPIs />
                <div className='LicenseManagement-Details-fueInventory'>
                  <FUEInventoryControls
                    onRunStarSimulation={() =>
                      setIsStarSimulationModalOpen(true)
                    }
                    onExport={() => {
                      // Default columns for the current identity category
                      const defaultColumns = [
                        'userId',
                        'fullName',
                        'department',
                        'identityType',
                        'currentTier',
                        'currentFue',
                        'starSimulated',
                        'recommendedTier',
                        'cost',
                        'status',
                      ];

                      const columnOrder: Record<string, number> = {};

                      defaultColumns.forEach((col, index) => {
                        columnOrder[col] = index + 1;
                      });

                      exportLicenseInventory({
                        reportType: 'LICENSE_PERMISSION',
                        format: 'EXCEL',
                        columnConfig: {
                          columnOrder,
                          selectedColumns: defaultColumns,
                          columnDisplay: {},
                        },
                        filters: {},
                        formattingOptions: {
                          excelOptions: {
                            cellFormatting: {
                              data: {
                                dateFormat: 'yyyy-MM-dd HH:mm:ss',
                                numberFormat: '#,##0.00',
                              },
                            },
                          },
                        },
                        fileName: `License_Export_${new Date().getFullYear()}`,
                        timezone: 'UTC',
                      }).catch(() => {
                        // Error handled inside hook via banner
                      });
                    }}
                    isExporting={isExporting}
                    isFilterBarOpen={isFUEFilterBarOpen}
                    onToggleFilter={() =>
                      setIsFUEFilterBarOpen((prev) => !prev)
                    }
                    hasActiveFilters={fueHasActiveFilters}
                  />
                  <FUEInventoryTable
                    vendor={currentVendor.toLowerCase()}
                    onRowClick={(user) => {
                      setSelectedFUEUser(user);
                      setIsFUEUserPanelOpen(true);
                    }}
                    isFilterBarOpen={isFUEFilterBarOpen}
                    filterSelections={fueFilterSelections}
                    onFilterChange={handleFUEFilterChange}
                    onClearFilters={handleFUEClearFilters}
                    enabledFilterIds={fueEnabledFilterIds}
                    onEnabledFilterIdsChange={setFueEnabledFilterIds}
                  />
                </div>
              </>
            )}
            {activeTab === 'role-intelligence' && (
              <RoleIntelligenceKPIs
                onRoleRowClick={(role: RoleIntelligenceRow) => {
                  setSelectedRole(role);
                  setIsRolePanelOpen(true);
                }}
              />
            )}
          </div>
        </div>

        <AddLicensePanel
          isOpen={isAddLicensePanelOpen}
          onClose={handleCloseAddLicensePanel}
          onSave={handleSaveLicense}
          vendor={currentVendor.toLowerCase()}
          endpointOptions={endpointOptions}
          isSaving={isSavingLicense}
        />

        <FUEInventoryUserPanel
          isOpen={isFUEUserPanelOpen}
          onClose={() => setIsFUEUserPanelOpen(false)}
          user={selectedFUEUser}
          vendor={currentVendor.toLowerCase()}
        />

        <RunStarSimulationModal
          isOpen={isStarSimulationModalOpen}
          onClose={() => setIsStarSimulationModalOpen(false)}
          vendor={vendor ?? 'sap'}
          cloudLicenseKey={1}
          onStartSimulation={handleStartSimulation}
          onSimulationSuccess={() => {
            setActiveTab('overview');
          }}
        />

        <RoleIntelligencePanel
          isOpen={isRolePanelOpen}
          onClose={() => setIsRolePanelOpen(false)}
          role={selectedRole}
        />
      </>
    );
  };

  return (
    <div className='LicenseManagement-Details' data-theme='dark'>
      {(isSavingLicense || isRunningSimulation) && (
        <div className='LicenseManagement-Details-loaderOverlay'>
          <Loader kind='loop' format='main' color='brand' />
        </div>
      )}
      {renderMainContent()}
      <SaveLicenseConfirmModal
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onRunSimulation={handleRunSimulation}
        licenseData={pendingLicenseData}
      />
    </div>
  );
}

export default Details;
